<template>
  <div class="tab-panel-body">
    <!-- Header: Add button or back arrow -->
    <div v-if="!showForm" class="config-section-header">
      <div class="config-section-header__left">
        <v-icon small class="mr-2" color="text_secondary">mdi-view-grid</v-icon>
        <span class="config-section-label">Configurações do mosaico</span>
        <v-chip v-if="configsMosaico.length > 0" x-small class="ml-2" color="surface_tertiary">
          {{ configsMosaico.length }}
        </v-chip>
      </div>
      <v-btn
        v-if="!loadingConfigs"
        small
        color="primary"
        class="mosaic-add-btn"
        @click.prevent="criarNovaConfiguracao"
      >
        <v-icon small class="mr-1">mdi-plus</v-icon>
        Nova configuração
      </v-btn>
    </div>

    <div v-if="showForm" class="mosaic-form-header">
      <v-btn icon small @click="closeForm" class="mosaic-back-btn">
        <v-icon small>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="mosaic-form-title">
        {{ editConfig ? "Editar configuração" : "Nova configuração" }}
      </span>
    </div>

    <!-- Config list -->
    <div v-if="!showForm" class="mosaic-list">
      <template v-if="loadingConfigs">
        <div v-for="i in 3" :key="'skeleton-' + i" class="mosaic-config-card mosaic-skeleton">
          <v-skeleton-loader type="list-item-three-line" />
        </div>
      </template>

      <transition-group
        v-if="!loadingConfigs && configsMosaico.length > 0"
        name="mosaic-reorder"
        tag="div"
        class="mosaic-transition-list"
      >
        <div
          v-for="config in configsMosaico"
          :key="config.idConfigMosaico"
          class="mosaic-config-card"
          :class="{ 'mosaic-config-active': config.flgAtivo }"
        >
          <div class="mosaic-config-header">
            <div class="mosaic-config-name">
              <span class="mosaic-config-title">
                {{ config.dscNomeConfig.length > 50 ? config.dscNomeConfig.substring(0, 49) + '…' : config.dscNomeConfig }}
              </span>
              <span v-if="config.flgAtivo" class="sc-badge sc-badge--primary ml-2">Padrão</span>
            </div>
            <div class="mosaic-config-actions">
              <div class="table-action-box" @click.prevent="editarConfiguracao(config)">
                <v-icon small>mdi-pencil</v-icon>
                <span class="table-action-label">Editar</span>
              </div>
              <div class="table-action-box table-action-delete" @click.prevent="setDeleteConfirm(config.idConfigMosaico, true)">
                <v-icon small>mdi-delete-outline</v-icon>
                <span class="table-action-label">Excluir</span>
              </div>
            </div>
          </div>

          <div class="mosaic-color-swatches">
            <div class="mosaic-swatch" v-for="property in Object.keys(colorKeys)" :key="colorKeys[property]">
              <div class="mosaic-swatch-dot" :style="{ 'background-color': config[colorKeys[property]] }"></div>
              <span class="mosaic-swatch-label">{{ property }}</span>
            </div>
            <div class="mosaic-swatch">
              <div
                v-if="config.urlImgBackground"
                class="mosaic-swatch-dot mosaic-swatch-image"
                :style="{ 'background-image': `url(${config.urlImgBackground})` }"
              >
                <v-icon x-small color="white" class="mosaic-image-fallback">mdi-image</v-icon>
              </div>
              <div v-else class="mosaic-swatch-dot" :style="{ 'background-color': config.dscCorBackground }"></div>
              <span class="mosaic-swatch-label">{{ config.urlImgBackground ? 'imagem' : 'fundo' }}</span>
            </div>
          </div>

          <div v-if="!config.flgAtivo" class="mosaic-config-footer">
            <v-btn
              text
              x-small
              color="primary"
              @click="ativarConfiguracao(config.idConfigMosaico)"
              :loading="loadingAtivar"
              class="mosaic-set-default-btn"
            >
              <v-icon x-small class="mr-1">mdi-check-circle-outline</v-icon>
              Definir como padrão
            </v-btn>
          </div>
        </div>
      </transition-group>

      <!-- Empty state -->
      <div v-if="!loadingConfigs && configsMosaico.length === 0" class="sc-empty-state">
        <v-icon size="48" color="text_secondary" class="mb-3">mdi-view-grid-plus-outline</v-icon>
        <span class="sc-empty-state__title">Nenhuma configuração</span>
        <span class="sc-empty-state__subtitle">Use "Nova configuração" acima para criar a primeira</span>
      </div>
    </div>

    <!-- Create/Edit form -->
    <ConfigMosaicoForm
      v-if="showForm"
      :cod-producao="codProducao"
      :sigla-producao="siglaProducao"
      :edit-config="editConfig"
      @saved="onFormSaved"
      @cancel="closeForm"
    />

    <ModalConfirmAction
      :visible="confirmDeleteObject !== null"
      title="Excluir configuração"
      message="Tem certeza que deseja excluir esta configuração de mosaico?"
      confirm-text="Excluir"
      confirm-color="red"
      :loading="loadingDelete"
      @confirm="excluirConfiguracao(confirmDeleteObject)"
      @cancel="confirmDeleteObject = null"
    />
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { mapActions, mapGetters } from "vuex";
import ConfigMosaicoForm from "./ConfigMosaicoForm.vue";

export default {
  name: "ConfigTabMosaico",
  components: {
    ConfigMosaicoForm,
    ModalConfirmAction: () => import("@/components/Modal/ModalConfirmAction.vue"),
  },
  props: {
    codProducao: { type: Number, required: true },
    siglaProducao: { type: String, required: true },
    active: { type: Boolean, default: false },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.fetch();
      },
    },
    codProducao() {
      if (this.active) this.fetch();
    },
  },
  data() {
    return {
      showForm: false,
      editConfig: null,
      loadingAtivar: false,
      confirmDeleteObject: null,
      loadingDelete: false,
      loadingConfigs: false,
      colorKeys: {
        card: "dscCorCard",
        fonte: "dscCorFonte",
      },
    };
  },
  computed: {
    ...mapGetters({
      configsMosaicoRaw: "mosaico/getConfigsMosaico",
    }),
    configsMosaico() {
      if (!this.configsMosaicoRaw) return [];
      return [...this.configsMosaicoRaw].sort((a, b) => {
        if (a.flgAtivo && !b.flgAtivo) return -1;
        if (!a.flgAtivo && b.flgAtivo) return 1;
        return 0;
      });
    },
  },
  methods: {
    ...mapActions({
      getAllConfigsMosaico: "mosaico/getAllConfigsMosaico",
    }),
    async fetch(silent = false) {
      if (!silent) this.loadingConfigs = true;
      await this.getAllConfigsMosaico({ cod_producao: this.codProducao }).catch(() => {
        this.$toast.error("Não foi possível obter as configurações");
      });
      if (!silent) this.loadingConfigs = false;
    },
    getDeleteConfirm(configId) {
      return this.confirmDeleteObject === configId;
    },
    setDeleteConfirm(configId, value) {
      this.confirmDeleteObject = value ? configId : null;
    },
    criarNovaConfiguracao() {
      this.editConfig = null;
      this.showForm = true;
    },
    editarConfiguracao(config) {
      this.editConfig = config;
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.editConfig = null;
    },
    async onFormSaved() {
      this.closeForm();
      await this.fetch();
    },
    async excluirConfiguracao(configId) {
      this.loadingDelete = true;
      try {
        await apiClient.delete(`/producao/${this.codProducao}/mosaicoConfig/${configId}`);
        this.$toast.success("Configuração excluída com sucesso");
      } catch (error) {
        this.$toast.error(`Erro durante exclusão: ${error.message}`);
      } finally {
        this.confirmDeleteObject = null;
        this.loadingDelete = false;
        await this.fetch();
      }
    },
    async ativarConfiguracao(configId) {
      this.loadingAtivar = true;
      try {
        const response = await apiClient.patch(
          `/producao/${this.codProducao}/mosaicoConfig/${configId}/ativar`
        );
        if (response.status === 204) {
          await this.fetch(true);
        }
      } catch (error) {
        this.$toast.error(error.message);
      } finally {
        this.loadingAtivar = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mosaic-add-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: var(--sc-radius-md);
}

/* Form header */
.mosaic-form-header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--sc-border-subtle);
}

.mosaic-back-btn {
  background: var(--sc-surface-tint-hover) !important;
  margin-right: 12px;
}

.mosaic-form-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--v-text_primary-base);
}

/* Config list */
.mosaic-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Config card */
.mosaic-transition-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Reorder transition */
.mosaic-reorder-move {
  transition: transform 0.4s ease;
}

.mosaic-config-card {
  background: var(--v-surface_tertiary-base);
  border-radius: var(--sc-radius-lg);
  padding: 16px;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:hover {
    border-color: var(--sc-border-emphasis);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
}

.mosaic-config-active {
  border-color: rgba(var(--sc-color-primary-rgb),0.2);
}

.mosaic-skeleton {
  padding: 8px;
}

/* Card header */
.mosaic-config-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.mosaic-config-name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.mosaic-config-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Action buttons */
.mosaic-config-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}


/* Color swatches */
.mosaic-color-swatches {
  display: flex;
  gap: 16px;
  padding: 10px 12px;
  background: var(--sc-surface-tint);
  border-radius: var(--sc-radius-md);
}

.mosaic-swatch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mosaic-swatch-dot {
  width: 24px;
  height: 24px;
  border-radius: var(--sc-radius-sm);
  border: 1px solid var(--sc-border-hover);
  flex-shrink: 0;
}

.mosaic-swatch-image {
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;

  .mosaic-image-fallback {
    opacity: 0;
  }

  &[style*="url()"], &[style*="url(\"\")"] {
    background: var(--sc-surface-tint-strong);
    .mosaic-image-fallback { opacity: 1; }
  }
}

.mosaic-swatch-label {
  font-size: 0.75rem;
  color: var(--v-text_secondary-base);
  text-transform: capitalize;
}

/* Footer */
.mosaic-config-footer {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.mosaic-set-default-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

</style>
